import React, { Fragment, useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';
import { toast } from 'react-toastify';
import gtu from '../../images/portfolio/gtu.jpg';
import { formatImageUrl, stripHtml } from '../../utils/security';

import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const ProjectDetails = () => {
    const { key, lang: urlLang } = useParams();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const lang = urlLang || i18n.language || 'en';

    const copyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success(t('portfolio.linkCopied') || 'Link copied!');
    };

    useEffect(() => {
        if (urlLang && (urlLang === 'en' || urlLang === 'ka')) {
            i18n.changeLanguage(urlLang);
        }
    }, [urlLang, i18n]);

    useEffect(() => {
        if (!key || key === 'undefined' || key === 'null') { setLoading(false); return; }
        const fetchProject = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}portfolios.php?slug=${key}&lang=${lang}`);
                const data = await response.json();
                if (data && !data.error) {
                    setProject({
                        ...data,
                        title: stripHtml(data.title),
                        summary: stripHtml(data.summary),
                        image: formatImageUrl(data.image, gtu)
                    });

                    // Log view if user is logged in
                    if (user) {
                        fetch(`${API_URL}users.php`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'log_view',
                                user_id: user.id,
                                entity_type: 'portfolio',
                                entity_id: data.id
                            })
                        }).catch(err => console.error("Logging error:", err));
                    }
                } else {
                    setProject(null);
                }
            } catch (error) {
                console.error('Error fetching project:', error);
                setProject(null);
            }
            setLoading(false);
            window.scrollTo(0, 0);
        };
        fetchProject();
    }, [key, lang, user]);

    if (!key || key === 'undefined' || key === 'null') return <Navigate to={`/${lang}/404`} replace />;
    if (loading) return <ProjectDetailsSkeleton />;
    if (!project) return <Navigate to={`/${lang}/404`} replace />;

    return (
        <Fragment>
            <Seo 
                title={project.seo_title || project.title}
                description={project.seo_description || project.summary}
                keywords={project.seo_keywords || ""}
                url={`/${lang}/portfolio/${key}`}
            />
            <Layout>
                <main className="page_content pt-150 pb-120">
                    <div className="container">
                        
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                            >
                                {/* Floating Prev / Next Navigation */}
                                {project.prev_slug && (
                                    <Link
                                        to={`/${lang}/portfolio/${project.prev_slug}`}
                                        className="pd-nav pd-nav-prev"
                                        aria-label={t('portfolio.prevProject', 'Previous project')}
                                    >
                                        <span className="pd-nav-icon"><i className="fas fa-arrow-left"></i></span>
                                        <span className="pd-nav-label">
                                            <small>{t('portfolio.prevProject', 'PREVIOUS')}</small>
                                            <strong>{project.prev_title || project.prev_slug}</strong>
                                        </span>
                                    </Link>
                                )}

                                {project.next_slug && (
                                    <Link
                                        to={`/${lang}/portfolio/${project.next_slug}`}
                                        className="pd-nav pd-nav-next"
                                        aria-label={t('portfolio.nextProject', 'Next project')}
                                    >
                                        <span className="pd-nav-label">
                                            <small>{t('portfolio.nextProject', 'NEXT')}</small>
                                            <strong>{project.next_title || project.next_slug}</strong>
                                        </span>
                                        <span className="pd-nav-icon"><i className="fas fa-arrow-right"></i></span>
                                    </Link>
                                )}

                                {/* Title Section matches screenshot */}
                                <div className="row mb-5">
                                    <div className="col-12">
                                        <h1 className="text-white m-0 fw-bold" style={{fontSize: 'clamp(1.8rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-1px'}}>
                                            {project.title}
                                        </h1>
                                    </div>
                                </div>

                                {/* Browser Mockup matches screenshot */}
                                <div className="browser-mockup-main mb-80 rounded-4 overflow-hidden shadow-lg" style={{background: '#1a1a21', border: '1px solid rgba(255,255,255,0.05)'}}>
                                    <div className="mockup-header d-flex align-items-center px-4 py-3" style={{background: '#1a1a21', borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                                        <div className="mockup-dots d-flex gap-2">
                                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#41414b', display: 'block'}}></span>
                                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#41414b', display: 'block'}}></span>
                                            <span style={{width: '12px', height: '12px', borderRadius: '50%', background: '#41414b', display: 'block'}}></span>
                                        </div>
                                        <div className="mockup-address-bar ms-4 px-3 py-1 rounded" style={{background: '#111116', color: '#6c757d', fontSize: '13px', width: '350px', maxWidth: '50%'}}>
                                            {project.live_url ? project.live_url : 'https://...'}
                                        </div>
                                    </div>
                                    <div className="mockup-body">
                                        <img src={project.image} alt={project.title} className="w-100" style={{objectFit: 'cover', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem'}} />
                                        {/* Bottom Green Border from screenshot (using Rustavi colors or default cyan) */}
                                        <div style={{height: '6px', width: '100%', background: '#20c997', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem'}}></div>
                                    </div>
                                </div>

                                {/* Content Area matches screenshot */}
                                <div className="row mb-80">
                                    <div className="col-lg-8">
                                        <div className="pd-gradient-card p-4 p-lg-5 mb-4">
                                            <h3 className="text-white-50 fw-bold mb-4" style={{fontSize: '1.8rem'}}>{t('portfolio.overview', 'Overview')}</h3>
                                            <div className="text-white" style={{lineHeight: 1.8, fontSize: '1.1rem'}} dangerouslySetInnerHTML={{ __html: project.content || project.summary }}></div>
                                        </div>
                                        
                                        {project.challenge && (
                                            <div className="pd-gradient-card p-4 p-lg-5 mb-4">
                                                <h3 className="text-white-50 fw-bold mb-4" style={{fontSize: '1.8rem'}}>{t('portfolio.challenge', 'Challenge')}</h3>
                                                <div className="text-white" style={{lineHeight: 1.8, fontSize: '1.1rem'}} dangerouslySetInnerHTML={{ __html: project.challenge }}></div>
                                            </div>
                                        )}
                                        {project.solution && (
                                            <div className="pd-gradient-card p-4 p-lg-5 mb-4">
                                                <h3 className="text-white-50 fw-bold mb-4" style={{fontSize: '1.8rem'}}>{t('portfolio.solution', 'Solution')}</h3>
                                                <div className="text-white" style={{lineHeight: 1.8, fontSize: '1.1rem'}} dangerouslySetInnerHTML={{ __html: project.solution }}></div>
                                            </div>
                                        )}
                                        {project.results && (
                                            <div className="pd-gradient-card p-4 p-lg-5 mb-4">
                                                <h3 className="text-white-50 fw-bold mb-4" style={{fontSize: '1.8rem'}}>{t('portfolio.results', 'Results')}</h3>
                                                <div className="text-white" style={{lineHeight: 1.8, fontSize: '1.1rem'}} dangerouslySetInnerHTML={{ __html: project.results }}></div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-lg-4 mt-5 mt-lg-0">
                                        <div className="p-4 rounded-4 pd-gradient-card" style={{background: '#080c27', border: '1px solid rgba(255,255,255,0.03)'}}>
                                            <div className="mb-4">
                                                <span className="d-block text-white-50 text-uppercase fw-bold mb-1" style={{fontSize: '10px', letterSpacing: '1px'}}>{t('portfolio.categoryLabel', 'Category').toUpperCase()}</span>
                                                <strong className="text-white d-block" style={{fontSize: '1.2rem'}}>{t(`portfolio.${project.category}`, project.category)}</strong>
                                            </div>
                                            <div className="mb-4">
                                                <span className="d-block text-white-50 text-uppercase fw-bold mb-1" style={{fontSize: '10px', letterSpacing: '1px'}}>{t('portfolio.location', 'Location').toUpperCase()}</span>
                                                <strong className="text-white d-block" style={{fontSize: '1.2rem'}}>{project.location || t('portfolio.locationValue', 'Georgia')}</strong>
                                            </div>
                                            {project.live_url && (
                                                <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="btn text-white rounded-pill px-4 py-3 fw-bold shadow-lg w-100" style={{background: '#6c5ce7', border: 'none', transition: '0.3s'}}>
                                                    {t('portfolio.viewWebsite', 'View Website')} <i className="fas fa-external-link-square-alt ms-2"></i>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Share Section matches screenshot */}
                                <div className="text-center p-5 rounded-4 pd-gradient-card" style={{background: '#080c27', border: '1px solid rgba(255,255,255,0.03)'}}>
                                    <h4 className="text-white mb-4 fw-bold">{t('portfolio.shareProject', 'Share Project')}</h4>
                                    <div className="d-flex justify-content-center gap-3">
                                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="share-btn text-white text-decoration-none rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', background: '#1e115b'}}>
                                            <i className="fab fa-facebook-f"></i>
                                        </a>
                                        <a href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${project.title}`} target="_blank" rel="noopener noreferrer" className="share-btn text-white text-decoration-none rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', background: '#1e115b'}}>
                                            <i className="fab fa-twitter"></i>
                                        </a>
                                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="share-btn text-white text-decoration-none rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', background: '#1e115b'}}>
                                            <i className="fab fa-linkedin-in"></i>
                                        </a>
                                        <button onClick={copyToClipboard} className="share-btn border-0 text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', background: '#1e115b'}}>
                                            <i className="fas fa-link"></i>
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                    </div>
                </main>
            </Layout>

            <style>{`
                .share-btn { transition: 0.3s; }
                .share-btn:hover { background: #6c5ce7 !important; transform: translateY(-3px); }
                .border-white-10 { border-color: rgba(255, 255, 255, 0.1) !important; }
            `}</style>
        </Fragment>
    );
};

export default ProjectDetails;

const ProjectDetailsSkeleton = () => (
    <Layout>
        <main className="page_content pt-150 pb-120">
            <div className="container">

                {/* Top navigation */}
                <div className="d-flex justify-content-between align-items-center mb-5">
                    <div className="pd-skeleton pd-skeleton-nav" />
                    <div className="pd-skeleton pd-skeleton-pill" />
                    <div className="pd-skeleton pd-skeleton-nav" />
                </div>

                {/* Title */}
                <div className="row align-items-end mb-5">
                    <div className="col-lg-8">
                        <div className="pd-skeleton pd-skeleton-title" />
                        <div className="pd-skeleton pd-skeleton-title-short mt-3" />
                    </div>
                    <div className="col-lg-4 text-lg-end">
                        <div className="pd-skeleton pd-skeleton-btn ms-auto" />
                    </div>
                </div>

                {/* Browser mockup */}
                <div className="pd-skeleton pd-skeleton-mockup mb-80 rounded-4" />

                {/* Content */}
                <div className="row mb-80">
                    <div className="col-lg-8">
                        <div className="pd-skeleton pd-skeleton-h mb-4" />
                        <div className="pd-skeleton pd-skeleton-line" />
                        <div className="pd-skeleton pd-skeleton-line short" />
                        <div className="pd-skeleton pd-skeleton-line" />
                        <div className="pd-skeleton pd-skeleton-h mt-5 mb-4" />
                        <div className="pd-skeleton pd-skeleton-line" />
                        <div className="pd-skeleton pd-skeleton-line short" />
                    </div>
                    <div className="col-lg-4">
                        <div className="pd-skeleton pd-skeleton-sidebar rounded-4" />
                    </div>
                </div>

                {/* Share */}
                <div className="pd-skeleton pd-skeleton-share rounded-4" />
            </div>
        </main>
        <style>{`
            .pd-skeleton {
                position: relative;
                overflow: hidden;
                background: rgba(255, 255, 255, 0.05);
                border-radius: 10px;
            }
            .pd-skeleton::after {
                content: '';
                position: absolute;
                inset: 0;
                transform: translateX(-100%);
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
                animation: pd-shimmer 1.4s infinite;
            }
            @keyframes pd-shimmer {
                100% { transform: translateX(100%); }
            }
            .pd-skeleton-nav { width: 110px; height: 16px; }
            .pd-skeleton-pill { width: 120px; height: 32px; border-radius: 50px; }
            .pd-skeleton-title { height: 64px; width: 82%; border-radius: 12px; }
            .pd-skeleton-title-short { height: 34px; width: 45%; border-radius: 12px; }
            .pd-skeleton-btn { width: 150px; height: 52px; border-radius: 50px; }
            .pd-skeleton-mockup { height: 460px; background: #1a1a21; border: 1px solid rgba(255, 255, 255, 0.05); }
            .pd-skeleton-h { height: 34px; width: 220px; border-radius: 8px; }
            .pd-skeleton-line { height: 16px; width: 100%; margin-bottom: 14px; }
            .pd-skeleton-line.short { width: 70%; }
            .pd-skeleton-sidebar { height: 180px; background: #080c27; border: 1px solid rgb(8 11 37); }
            .pd-skeleton-share { height: 200px; background: #080c27; border: 1px solid rgb(8 11 37); }
            @media (max-width: 991px) {
                .pd-skeleton-mockup { height: 320px; }
                .pd-skeleton-sidebar { margin-top: 20px; }
            }
        `}</style>
    </Layout>
);

